import type { CompanyResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2 } from "@mui/material";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
	type CompanyFormModel,
	getCompanyFormSchema,
} from "./Schema/companySchema";

const companyResources = Resources.base.company;

type CompanyFormProps = {
	onSubmit: (data: CompanyFormModel) => void;
	defaultData?: CompanyResponseDto;
	isPending?: boolean;
};

export const CompanyForm: FC<CompanyFormProps> = ({
	onSubmit,
	defaultData,
	isPending = false,
}) => {
	const { t } = useResource();

	const form = useForm<CompanyFormModel>({
		resolver: zodResolver(getCompanyFormSchema()),
	});

	const { handleServerError } = useHandleServerError(form);

	const handleSubmit = () => {
		handleServerError(onSubmit);
	};

	const {
		control,
		formState: { errors },
		reset,
	} = form;

	useEffect(() => {
		if (defaultData) {
			reset(defaultData);
		}
	}, [defaultData, reset]);

	return (
		<Box component="form">
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
					<Input
						id="name"
						name="name"
						label={t(companyResources.detail.info.name)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="businessId"
						name="businessId"
						label={t(companyResources.detail.info.businessId)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="vatId"
						name="vatId"
						label={t(companyResources.detail.info.vatId)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="email"
						name="email"
						label={t(companyResources.detail.info.email)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="phone"
						name="phone"
						label={t(companyResources.detail.info.phone)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="invoiceDueDays"
						name="invoiceDueDays"
						label={t(companyResources.detail.info.invoiceDueDays)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
					<Input
						id="country"
						name="country"
						label={t(companyResources.detail.info.country)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="city"
						name="city"
						label={t(companyResources.detail.info.city)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="postCode"
						name="postCode"
						label={t(companyResources.detail.info.postCode)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="street"
						name="street"
						label={t(companyResources.detail.info.street)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
					<Input
						id="buildingNumber"
						name="buildingNumber"
						label={t(companyResources.detail.info.buildingNumber)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<Button
				variant="contained"
				color="primary"
				onClick={handleSubmit}
				loading={isPending}
				sx={{ mt: 2 }}
			>
				{t(companyResources.shared.save)}
			</Button>
		</Box>
	);
};
